import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { showMessage } from 'react-native-flash-message';

import { clearNewlyEarnedBadges } from 'store/relicVault';

/**
 * Announces badges the server awarded while the learner was playing.
 *
 * Badges are earned server-side - a streak reaching its next milestone, a
 * lesson finished - so the app only finds out when it next reads the vault.
 * Without this the learner has to open the Collection Store and notice a tile
 * has changed, which is no way to be told you earned something.
 *
 * The first read after opening the world is a baseline, so nothing is
 * announced for badges already earned (see hydrateRelicVault).
 */
export function useBadgeAnnouncements() {
  const dispatch = useDispatch();
  const newlyEarnedBadgeIds = useSelector(
    state => state.relicVault.newlyEarnedBadgeIds,
  );
  const badgeCategories = useSelector(state => state.relicVault.badgeCategories);

  useEffect(() => {
    if (!newlyEarnedBadgeIds.length) return;

    const byId = new Map();
    for (const category of badgeCategories) {
      for (const badge of category.badges || []) byId.set(badge.badgeId, badge);
    }

    const names = newlyEarnedBadgeIds
      .map(id => byId.get(id)?.name)
      .filter(Boolean);

    const count = newlyEarnedBadgeIds.length;
    showMessage({
      message: count > 1 ? `${count} new badges!` : 'New badge earned!',
      // Fall back to the count when the catalog could not name them.
      description: names.length
        ? names.join(', ')
        : 'Open the Collection Store to see it.',
      type: 'success',
      duration: 5000,
    });

    dispatch(clearNewlyEarnedBadges());
  }, [badgeCategories, dispatch, newlyEarnedBadgeIds]);
}

export default useBadgeAnnouncements;
